export const CARD_SIZES = {
  square: { w: 1080, h: 1080, label: "Square" },
  story: { w: 1080, h: 1920, label: "Story" },
};

const RED = "#FF3B30";
const PAD = 96;

function drawCoverFit(ctx, img, x, y, w, h) {
  const r = Math.max(w / img.width, h / img.height);
  const iw = img.width * r;
  const ih = img.height * r;
  ctx.drawImage(img, x + (w - iw) / 2, y + (h - ih) / 2, iw, ih);
}

// Downscale-then-upscale blur works in every browser (Safari lacks ctx.filter).
function drawBlurredCover(ctx, img, w, h) {
  const tiny = document.createElement("canvas");
  tiny.width = 36;
  tiny.height = Math.round((36 * h) / w);
  const t = tiny.getContext("2d");
  drawCoverFit(t, img, 0, 0, tiny.width, tiny.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(tiny, -40, -40, w + 80, h + 80);
  ctx.fillStyle = "rgba(10,10,10,0.55)";
  ctx.fillRect(0, 0, w, h);
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, "rgba(0,0,0,0.25)");
  g.addColorStop(1, "rgba(0,0,0,0.6)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
}

function drawBackground(ctx, style, img, w, h) {
  if (style === "cover" && img) return drawBlurredCover(ctx, img, w, h);
  if (style === "gradient") {
    const g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, "#FF3B30");
    g.addColorStop(0.45, "#8A1238");
    g.addColorStop(1, "#140812");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    const r = ctx.createRadialGradient(w, 0, 0, w, 0, w);
    r.addColorStop(0, "rgba(255,190,120,0.35)");
    r.addColorStop(1, "rgba(255,190,120,0)");
    ctx.fillStyle = r;
    ctx.fillRect(0, 0, w, h);
    return;
  }
  ctx.fillStyle = "#0A0A0A";
  ctx.fillRect(0, 0, w, h);
  const r = ctx.createRadialGradient(0, h, 0, 0, h, w * 0.9);
  r.addColorStop(0, "rgba(255,59,48,0.28)");
  r.addColorStop(1, "rgba(255,59,48,0)");
  ctx.fillStyle = r;
  ctx.fillRect(0, 0, w, h);
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawHeader(ctx, { img, title, artist, style }, w, y) {
  const s = 132;
  ctx.save();
  roundRect(ctx, PAD, y, s, s, 14);
  ctx.clip();
  if (img) drawCoverFit(ctx, img, PAD, y, s, s);
  else {
    ctx.fillStyle = "#1A1A1A";
    ctx.fillRect(PAD, y, s, s);
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.font = "900 80px Outfit";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText((title || "?").trim().charAt(0).toUpperCase(), PAD + s / 2, y + s / 2 + 4);
  }
  ctx.restore();
  const tx = PAD + s + 32;
  const maxW = w - tx - PAD;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "800 46px Outfit";
  ctx.fillText(ellipsize(ctx, title || "Untitled", maxW), tx, y + 62);
  ctx.fillStyle = style === "gradient" ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.55)";
  ctx.font = "500 24px 'IBM Plex Mono'";
  ctx.fillText(ellipsize(ctx, (artist || "unknown artist").toUpperCase(), maxW), tx, y + 108);
}

function ellipsize(ctx, text, maxW) {
  if (ctx.measureText(text).width <= maxW) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(t + "…").width > maxW) t = t.slice(0, -1);
  return t + "…";
}

function wrap(ctx, text, maxW) {
  const words = text.split(/\s+/).filter(Boolean);
  const out = [];
  let cur = "";
  words.forEach((word) => {
    const next = cur ? `${cur} ${word}` : word;
    if (ctx.measureText(next).width <= maxW || !cur) cur = next;
    else {
      out.push(cur);
      cur = word;
    }
  });
  if (cur) out.push(cur);
  return out;
}

// groups: array of arrays of line strings; gaps between groups = non-adjacent picks
function layoutLyrics(ctx, groups, maxW, maxH, startSize) {
  for (let size = startSize; size >= 22; size -= 2) {
    ctx.font = `800 ${size}px Outfit`;
    const lh = size * 1.18;
    const lineGap = size * 0.28;
    const groupGap = size * 0.9;
    const rows = [];
    let h = 0;
    groups.forEach((g, gi) => {
      if (gi > 0) {
        rows.push({ gap: true, y: h + groupGap / 2 });
        h += groupGap;
      }
      g.forEach((line, li) => {
        if (li > 0) h += lineGap;
        wrap(ctx, line, maxW).forEach((part) => {
          rows.push({ text: part, y: h });
          h += lh;
        });
      });
    });
    if (h <= maxH || size <= 22) return { rows, size, height: h, lh };
  }
  return { rows: [], size: 22, height: 0, lh: 26 };
}

export function renderCard(canvas, { groups, title, artist, img, style, size }) {
  const { w, h } = CARD_SIZES[size];
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  drawBackground(ctx, style, img, w, h);

  const headerY = size === "story" ? 220 : PAD;
  drawHeader(ctx, { img, title, artist, style }, w, headerY);

  const footerH = 90;
  const top = headerY + 132 + (size === "story" ? 120 : 72);
  const bottom = h - PAD - footerH - (size === "story" ? 160 : 24);
  const maxW = w - PAD * 2 - 40;
  const { rows, size: fs, height, lh } = layoutLyrics(
    ctx,
    groups,
    maxW,
    bottom - top,
    size === "story" ? 92 : 80
  );
  const y0 = top + Math.max(0, (bottom - top - height) / 2);

  ctx.fillStyle = style === "gradient" ? "#FFFFFF" : RED;
  ctx.fillRect(PAD, y0 + 6, 7, Math.max(lh, height - 12));

  ctx.font = `800 ${fs}px Outfit`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.shadowColor = "rgba(0,0,0,0.35)";
  ctx.shadowBlur = 24;
  rows.forEach((r) => {
    if (r.gap) {
      ctx.fillStyle = "rgba(255,255,255,0.45)";
      ctx.font = `600 ${Math.round(fs * 0.6)}px Outfit`;
      ctx.fillText("· · ·", PAD + 40, y0 + r.y - fs * 0.32);
      ctx.font = `800 ${fs}px Outfit`;
      return;
    }
    ctx.fillStyle = "#FFFFFF";
    ctx.fillText(r.text, PAD + 40, y0 + r.y);
  });
  ctx.shadowBlur = 0;

  const fy = h - PAD - (size === "story" ? 120 : 0);
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "900 40px Outfit";
  ctx.fillText("LYRIQ", PAD, fy);
  const lw = ctx.measureText("LYRIQ").width;
  ctx.fillStyle = style === "gradient" ? "#FFFFFF" : RED;
  ctx.beginPath();
  ctx.arc(PAD + lw + 14, fy - 12, 7, 0, Math.PI * 2);
  ctx.fill();
  ctx.textAlign = "right";
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = "500 22px 'IBM Plex Mono'";
  ctx.fillText("// SYNCED LYRICS", w - PAD, fy - 4);
}

export async function ensureCardFonts() {
  if (!document.fonts) return;
  await Promise.all([
    document.fonts.load("800 80px Outfit"),
    document.fonts.load("900 40px Outfit"),
    document.fonts.load("500 24px 'IBM Plex Mono'"),
  ]).catch(() => {});
}

export function groupSelected(lines, selected) {
  const idx = [...selected].sort((a, b) => a - b);
  const groups = [];
  idx.forEach((i, k) => {
    if (k === 0 || i !== idx[k - 1] + 1) groups.push([]);
    groups[groups.length - 1].push(lines[i].text);
  });
  return groups;
}
